import clsx from "clsx";
import {
  CircleAlert,
  Inbox,
  LoaderCircle,
  RefreshCw,
  SearchX,
  ShieldAlert,
  WifiOff,
  type LucideIcon,
} from "lucide-react";
import { useId, type ReactNode } from "react";
import { isClientApiError } from "../api/http.js";
import { Button, type ControlSize } from "./Button.js";

/**
 * Owns every non-happy state a data-backed panel can be in — loading, empty,
 * offline, signed out, permission denied, not found, conflict, refused
 * request, server error —
 * with plain-language copy and a retry action where retrying can help.
 * Feature components pass their query state and render only the happy path.
 *
 * Two kinds of failure are told apart. `error` means there is nothing to
 * show: the panel becomes the error state. `refreshError` means a later
 * refetch failed while earlier data is still on screen (React Query keeps
 * `data` and sets `status: "error"` on a failed refetch): the children stay
 * exactly as they are — an open dialog, typed quantities, the row someone is
 * reading — and a notice above them says the refresh failed and offers to
 * try again. A failed refresh must never tear down what a person is doing.
 */

export type ErrorKind =
  | "offline"
  | "signed_out"
  | "forbidden"
  | "not_found"
  | "conflict"
  | "invalid"
  | "server"
  | "unknown";

export type ErrorPresentation = Readonly<{
  kind: ErrorKind;
  title: string;
  description: string;
  retryable: boolean;
  retryLabel: string;
}>;

const OFFLINE: ErrorPresentation = {
  kind: "offline",
  title: "You're offline",
  description:
    "We can't reach the server right now. Check your connection, then try again.",
  retryable: true,
  retryLabel: "Try again",
};

/** Turns any thrown value into the copy a person should read. */
export function describeError(error: unknown): ErrorPresentation {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return OFFLINE;
  }

  if (!isClientApiError(error)) {
    return {
      kind: "unknown",
      title: "Something went wrong",
      description:
        error instanceof Error && error.message
          ? error.message
          : "The page could not finish what it was doing. Try again.",
      retryable: true,
      retryLabel: "Try again",
    };
  }

  if (error.status === 0) return OFFLINE;

  if (error.status === 401) {
    return {
      kind: "signed_out",
      title: "Your session ended",
      description:
        "Sessions end after 12 hours or when an administrator resets an account. Sign in again to keep working.",
      retryable: true,
      retryLabel: "Try again",
    };
  }

  if (error.status === 403) {
    return {
      kind: "forbidden",
      title: "You don't have access to this",
      description:
        "Your account can view and comment on every case, but changing order details is limited to Parts. Ask an administrator if you think this is a mistake.",
      retryable: false,
      retryLabel: "Try again",
    };
  }

  if (error.status === 404) {
    return {
      kind: "not_found",
      title: "We couldn't find that",
      description:
        "The record doesn't exist, or the link is out of date. Search for it instead.",
      retryable: false,
      retryLabel: "Try again",
    };
  }

  if (error.status === 409) {
    return {
      kind: "conflict",
      title: "This changed while you were looking",
      description:
        "Someone else updated it first. Reload the latest version before continuing.",
      retryable: true,
      retryLabel: "Reload",
    };
  }

  if (error.status === 422) {
    // A refused request (a report range the server will not answer, say):
    // the first field message names what to change, the server's own
    // sentence stands in when there is none, and sending the same request
    // again cannot help — the person has to change it.
    const fieldMessage = Object.values(error.fieldErrors).flat()[0];
    return {
      kind: "invalid",
      title: "The request can't be answered as asked",
      description: fieldMessage ?? error.message,
      retryable: false,
      retryLabel: "Try again",
    };
  }

  if (error.status >= 500) {
    return {
      kind: "server",
      title: "Something went wrong on our side",
      description:
        "The server couldn't complete the request. Try again in a moment.",
      retryable: true,
      retryLabel: "Try again",
    };
  }

  return {
    kind: "unknown",
    title: "Something went wrong",
    description:
      error.message || "The request could not be completed. Try again.",
    retryable: true,
    retryLabel: "Try again",
  };
}

/**
 * Copy for a sign-out that did not complete. Sign-out is the one action whose
 * failure must never look like success: the server still holds the session,
 * so the person has to hear that they are still signed in before they walk
 * away from the computer.
 */
export function signOutFailureMessage(error: unknown): string {
  return `Sign out didn't finish, so you're still signed in on this computer. ${describeError(error).description}`;
}

const ERROR_ICONS: Record<ErrorKind, LucideIcon> = {
  offline: WifiOff,
  signed_out: ShieldAlert,
  forbidden: ShieldAlert,
  not_found: SearchX,
  conflict: RefreshCw,
  invalid: CircleAlert,
  server: CircleAlert,
  unknown: CircleAlert,
};

export interface AsyncPanelProps {
  isLoading?: boolean;
  /** A thrown query/mutation error with nothing loaded; presence puts the panel in its error state. */
  error?: unknown;
  /**
   * A refetch that failed while `children` still show earlier data: the
   * children stay and a {@link RefreshNotice} appears above them. `onRetry`
   * is offered there too.
   */
  refreshError?: unknown;
  /** The notice's first sentence: "Couldn't refresh this case." (default "Couldn't refresh."). */
  refreshTitle?: string;
  isEmpty?: boolean;
  onRetry?: () => void;
  loadingLabel?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  /** An action offered in the empty state (a "New order" link, for example). */
  emptyAction?: ReactNode;
  /**
   * The heading level of the empty and error titles: 2 under a page's `h1`
   * (the default), 3 inside a section that already has its own `h2`, so
   * the outline keeps the state as that section's content.
   */
  headingLevel?: 2 | 3;
  size?: ControlSize;
  className?: string;
  children: ReactNode;
}

export function AsyncPanel({
  isLoading = false,
  error,
  refreshError,
  refreshTitle,
  isEmpty = false,
  onRetry,
  loadingLabel = "Loading…",
  emptyTitle = "Nothing here yet",
  emptyDescription = "There is nothing to show for this view right now.",
  emptyAction,
  headingLevel = 2,
  size,
  className,
  children,
}: AsyncPanelProps) {
  const Title = headingLevel === 3 ? "h3" : "h2";
  if (isLoading) {
    return (
      <div
        className={clsx("async-panel", className)}
        data-state="loading"
        aria-busy="true"
      >
        <div role="status" aria-live="polite" className="async-panel__status">
          <LoaderCircle
            className="async-panel__spinner"
            aria-hidden="true"
            focusable="false"
          />
          <span>{loadingLabel}</span>
        </div>
      </div>
    );
  }

  if (error !== undefined && error !== null) {
    const presentation = describeError(error);
    const Icon = ERROR_ICONS[presentation.kind];
    return (
      <div
        className={clsx("async-panel", className)}
        data-state="error"
        data-error-kind={presentation.kind}
      >
        <div className="async-panel__message" role="alert">
          <Icon
            className="async-panel__icon"
            aria-hidden="true"
            focusable="false"
          />
          <Title className="async-panel__title">{presentation.title}</Title>
          <p className="async-panel__description">{presentation.description}</p>
          {presentation.retryable && onRetry ? (
            <Button
              variant="primary"
              size={size}
              onClick={onRetry}
              icon={<RefreshCw />}
            >
              {presentation.retryLabel}
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  const notice =
    refreshError !== undefined && refreshError !== null ? (
      <RefreshNotice
        error={refreshError}
        title={refreshTitle}
        onRetry={onRetry}
        size={size}
      />
    ) : null;

  if (isEmpty) {
    return (
      <>
        {notice}
        <div className={clsx("async-panel", className)} data-state="empty">
          <div className="async-panel__message">
            <Inbox
              className="async-panel__icon"
              aria-hidden="true"
              focusable="false"
            />
            <Title className="async-panel__title">{emptyTitle}</Title>
            <p className="async-panel__description">{emptyDescription}</p>
            {emptyAction}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {notice}
      {children}
    </>
  );
}

export interface RefreshNoticeProps {
  /** The refetch error; the notice classifies it with {@link describeError}. */
  error: unknown;
  /** One plain sentence naming what did not refresh. */
  title?: string;
  onRetry?: () => void;
  size?: ControlSize;
  className?: string;
}

/**
 * A refresh that failed while earlier data is still on screen: a polite
 * notice, not a takeover. Says what did not refresh, that what is shown is
 * what loaded earlier, why (in {@link describeError}'s words), and offers the
 * same retry the error state would.
 */
export function RefreshNotice({
  error,
  title = "Couldn't refresh.",
  onRetry,
  size,
  className,
}: RefreshNoticeProps) {
  const presentation = describeError(error);
  const Icon = ERROR_ICONS[presentation.kind];
  const titleId = useId();
  return (
    <div
      className={clsx("refresh-notice", className)}
      role="status"
      aria-labelledby={titleId}
      data-error-kind={presentation.kind}
    >
      <Icon
        className="refresh-notice__icon"
        aria-hidden="true"
        focusable="false"
      />
      <p className="refresh-notice__text">
        <strong id={titleId} className="refresh-notice__title">
          {title}
        </strong>{" "}
        Showing what loaded earlier. {presentation.description}
      </p>
      {presentation.retryable && onRetry ? (
        <Button
          variant="secondary"
          size={size}
          onClick={onRetry}
          icon={<RefreshCw />}
        >
          {presentation.retryLabel}
        </Button>
      ) : null}
    </div>
  );
}
